import { ReloadOutlined } from '@ant-design/icons';
import { notEmpty } from '@mahawi/eshop-common/src/not-empty';
import {
  type IReportPropertyLanguage,
  type ITranslation,
} from '@mahawi/eshop-common/src/types';
import { Button, Divider, Popconfirm, Space, Spin } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import Table from '../components/table';
import {
  AutomatProgressBar,
  type IAutomatProgress,
} from '../fragments/automat-progress-bar';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { useAutomatDocumentTitle } from '../hooks/use-automat-document-title';
import { type AppDispatch, type RootState } from '../reducers';
import { namesUpdate } from '../reducers/property/reducer';
import { reportPropertyLanguagesLoad } from '../reducers/report/reducer';
import { SPACING } from '../theme/tokens';

interface DataType extends IReportPropertyLanguage {
  key: string;
}

function openGoogleTranslateLink(
  fromLanguageCode: string,
  toLanguageCode: string,
  text: string,
): void {
  const gtLink = new URL('https://translate.google.com/');
  gtLink.searchParams.append('sl', fromLanguageCode.toLowerCase());
  gtLink.searchParams.append('tl', toLanguageCode.toLowerCase());
  gtLink.searchParams.append('text', text.trim());
  gtLink.searchParams.append('op', 'translate');

  window.open(gtLink.toString(), '_blank');
}

async function startAutomat(
  dataSource: DataType[],
  dispatch: AppDispatch,
  setRunning: React.Dispatch<React.SetStateAction<boolean>>,
  setProgress: React.Dispatch<React.SetStateAction<IAutomatProgress | null>>,
): Promise<void> {
  let total = 0;
  for (const record of dataSource) {
    const [n] = record.translation.names.filter(notEmpty);
    total +=
      n && n.value.length >= 2 ? record.languageType.missingCodes.length : 0;
  }

  setRunning(true);
  const startedAt = Date.now();
  setProgress({ current: 0, total, startedAt });
  let current = 0;

  for (const record of dataSource) {
    const names: ITranslation[] = [];

    for (const languageTypeCodeMissing of record.languageType.missingCodes) {
      const [translationName] = record.translation.names.filter(notEmpty);

      if (translationName && translationName.value.length >= 2) {
        await new Promise<void>((resolve): void => {
          window.addEventListener(
            'focus',
            async (): Promise<void> => {
              const text: string = await navigator.clipboard.readText();
              const tt: string = text.trim();

              if (tt) {
                names.push({
                  code: languageTypeCodeMissing,
                  value: tt,
                });
              }

              await navigator.clipboard.writeText('');

              resolve();
            },
            { once: true },
          );

          openGoogleTranslateLink(
            translationName.code,
            languageTypeCodeMissing,
            translationName.value,
          );
        });

        current++;
        setProgress({ current, total, startedAt });
      }
    }

    if (names.length === 0) {
      continue;
    }

    dispatch(namesUpdate(record.property.code, names));
  }

  setRunning(false);
  setProgress(null);
}

function ReportPropertyLanguages(): React.ReactElement {
  const dispatch = useAppDispatch();
  const reportPropertyLanguages = useSelector(
    (s: RootState) => s.Report.propertyLanguages,
  );
  const inProcess = useSelector((s: RootState) => s.Property.inProcess);

  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [columns, setColumns] = useState<ColumnsType<object>>([]);
  const [isRunning, setRunning] = useState<boolean>(false);
  const [progress, setProgress] = useState<IAutomatProgress | null>(null);

  useAutomatDocumentTitle(isRunning, progress);

  useEffect((): void => {
    if (!reportPropertyLanguages) {
      return;
    }

    const columnsUE: ColumnsType<object> = [
      {
        title: 'Code',
        dataIndex: ['property', 'code'],
        render: (_text, record: DataType): React.ReactElement => (
          <Link
            to={`/property/${record.property.code}`}
            openInNewTab
            label={record.property.code}
          />
        ),
      },
      {
        title: 'Name',
        dataIndex: ['translation', 'names', 0, 'value'],
        render: (_text, record: DataType): string => {
          if (record.translation.names.length === 0) {
            return '** MISSING ALL TRANSLATIONS **';
          }

          return `[${record.translation.names[0].code}] ${record.translation.names[0].value}`;
        },
      },
      {
        title: 'Missing languages',
        dataIndex: ['languageType', 'missingCodes'],
        render: (_text, record: DataType): string =>
          record.languageType.missingCodes.join(', '),
      },
      {
        title: 'Actions',
        render: (record: DataType): React.ReactElement => {
          return (
            <Popconfirm
              placement="bottom"
              title="Save as all active language types?"
              onConfirm={(): void => {
                const names: ITranslation[] =
                  record.languageType.missingCodes.map(
                    (code: string): ITranslation => ({
                      code,
                      value: record.translation.names[0].value,
                    }),
                  );

                dispatch(namesUpdate(record.property.code, names));
              }}
              okText="Yes"
              cancelText="No"
              disabled={inProcess}
            >
              <Button type="primary" disabled={inProcess}>
                Save as all same
              </Button>
            </Popconfirm>
          );
        },
      },
    ];

    setColumns(columnsUE);

    const dataSourceUE = reportPropertyLanguages?.map((item) => ({
      ...item,
      key: item.property.code,
    }));

    setDataSource(dataSourceUE);
  }, [reportPropertyLanguages, dispatch, inProcess]);

  return (
    <>
      <DetailPageHeader title="Report property languages" />

      <Space size={SPACING.lg} align="start">
        <Button
          type="primary"
          onClick={(): void => {
            dispatch(reportPropertyLanguagesLoad());
          }}
          disabled={!reportPropertyLanguages || inProcess}
        >
          <ReloadOutlined />
        </Button>
        <Button
          type="primary"
          onClick={(): void => {
            startAutomat(dataSource, dispatch, setRunning, setProgress);
          }}
          disabled={
            !reportPropertyLanguages ||
            isRunning ||
            inProcess ||
            dataSource.length === 0
          }
        >
          GT Automat
        </Button>
        <Spin size="small" spinning={isRunning || inProcess} />
      </Space>

      <AutomatProgressBar isRunning={isRunning} progress={progress} />

      <Divider />

      <Table
        columns={columns}
        dataSource={dataSource}
        isLoading={!reportPropertyLanguages}
      />
    </>
  );
}

export default ReportPropertyLanguages;
